import type { ServiceResponse } from './types';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

// Backend response types
interface AddressResponseDto {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface PickupLocationResponseDto {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode?: string;
  phone: string;
  email?: string;
  openingHours: string;
  mapUrl?: string;
  isActive: boolean;
  latitude?: number;
  longitude?: number;
  transportProviderName?: string;
}

interface SocialMediaResponseDto {
  facebook?: string;
  twitter?: string;
  instagram?: string;
  youtube?: string;
  linkedin?: string;
}

interface ShippingSettingsResponseDto {
  freeShippingThreshold?: number;
  flatShippingRate?: number;
  expressShippingRate?: number;
  estimatedDeliveryDays?: number;
  packagingCharges?: Array<{
    minAmount: number;
    maxAmount: number;
    charge: number;
  }>;
}

interface CompanyResponseDto {
  _id: string;
  id: string;
  companyName: string;
  email: string;
  phone: string;
  address: AddressResponseDto;
  pickupLocations: PickupLocationResponseDto[];
  createdAt: string;
  updatedAt: string;
  legalName?: string;
  registrationNumber?: string;
  taxId?: string;
  alternatePhone?: string;
  website?: string;
  billingAddress?: AddressResponseDto;
  logo?: string;
  favicon?: string;
  socialMedia?: SocialMediaResponseDto;
  businessHours?: any;
  policies?: any;
  seoSettings?: any;
  paymentSettings?: any;
  shippingSettings?: ShippingSettingsResponseDto;
  taxSettings?: any;
  whatsappNumber?: string;
  licenseNumber?: string;
}

// Frontend types
export interface CompanyAddress {
  type: 'registered' | 'warehouse' | 'retail';
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  landmark?: string;
  mapUrl?: string;
}

export interface ContactInfo {
  type: 'sales' | 'support' | 'general' | 'wholesale';
  phone: string;
  email: string;
  availability: string;
}

export interface SocialMedia {
  platform: string;
  url: string;
  handle: string;
}

export interface BusinessHours {
  day: string;
  open: string;
  close: string;
  isClosed: boolean;
}

export interface ShippingSettings {
  freeShippingThreshold?: number;
  flatShippingRate?: number;
  expressShippingRate?: number;
  estimatedDeliveryDays?: number;
  packagingCharges?: Array<{
    minAmount: number;
    maxAmount: number;
    charge: number;
  }>;
}

export interface CompanyInfo {
  name: string;
  tagline: string;
  logo: string;
  description: string;
  establishedYear: number;
  gstNumber: string;
  panNumber: string;
  licenseNumber: string;
  addresses: CompanyAddress[];
  contacts: ContactInfo[];
  socialMedia: SocialMedia[];
  businessHours: BusinessHours[];
  whatsappNumber: string;
  tollFreeNumber?: string;
  shippingSettings?: ShippingSettings;
}

export interface PickupLocation {
  id: string;
  name: string;
  address: string;
  area: string;
  city: string;
  state: string;
  zipCode?: string;
  pincode?: string;
  phone: string;
  timings: string;
  landmark?: string;
  mapUrl?: string;
  isActive: boolean;
  transportProviderName?: string;
}

class CompanyService {
  // Transform backend company to frontend format
  private transformCompany(company: CompanyResponseDto): CompanyInfo {
    // Build addresses array
    const addresses: CompanyAddress[] = [
      {
        type: 'registered',
        name: 'Head Office',
        addressLine1: company.address.street,
        city: company.address.city,
        state: company.address.state,
        pincode: company.address.zipCode,
        mapUrl: `https://maps.google.com/?q=${encodeURIComponent(`${company.address.street}, ${company.address.city} ${company.address.zipCode}`)}`
      }
    ];

    // Add billing address if different
    if (company.billingAddress) {
      addresses.push({
        type: 'retail',
        name: 'Billing Address',
        addressLine1: company.billingAddress.street,
        city: company.billingAddress.city,
        state: company.billingAddress.state,
        pincode: company.billingAddress.zipCode,
        mapUrl: `https://maps.google.com/?q=${encodeURIComponent(`${company.billingAddress.street}, ${company.billingAddress.city} ${company.billingAddress.zipCode}`)}`
      });
    }

    // Build contacts array
    const contacts: ContactInfo[] = [
      {
        type: 'general',
        phone: company.phone,
        email: company.email,
        availability: '10:00 AM - 6:00 PM'
      }
    ];

    if (company.alternatePhone) {
      contacts.push({
        type: 'support',
        phone: company.alternatePhone,
        email: company.email,
        availability: '24/7'
      });
    }

    // Build social media array
    const socialMedia: SocialMedia[] = [];
    if (company.socialMedia) {
      if (company.socialMedia.facebook) {
        socialMedia.push({
          platform: 'Facebook',
          url: company.socialMedia.facebook,
          handle: '@' + company.companyName.toLowerCase().replace(/\s+/g, '')
        });
      }
      if (company.socialMedia.instagram) {
        socialMedia.push({
          platform: 'Instagram',
          url: company.socialMedia.instagram,
          handle: '@' + company.companyName.toLowerCase().replace(/\s+/g, '')
        });
      }
      if (company.socialMedia.twitter) {
        socialMedia.push({
          platform: 'X',
          url: company.socialMedia.twitter,
          handle: '@' + company.companyName.toLowerCase().replace(/\s+/g, '')
        });
      }
      if (company.socialMedia.youtube) {
        socialMedia.push({
          platform: 'YouTube',
          url: company.socialMedia.youtube,
          handle: company.companyName
        });
      }
    }

    // Add WhatsApp if available
    if (company.whatsappNumber) {
      socialMedia.push({
        platform: 'WhatsApp',
        url: `https://wa.me/${company.whatsappNumber.replace(/[^0-9]/g, '')}`,
        handle: company.whatsappNumber
      });
    }

    // Parse business hours
    const businessHours: BusinessHours[] = this.parseBusinessHours(company.businessHours);

    return {
      name: company.companyName,
      tagline: 'Light Up Your Celebrations', // Default tagline
      logo: company.logo || '🎆',
      description: 'Your trusted partner for premium quality fireworks. We specialize in providing safe, certified, and spectacular fireworks for all your celebrations.',
      establishedYear: new Date(company.createdAt).getFullYear(),
      gstNumber: company.taxId || '',
      panNumber: company.registrationNumber || '',
      licenseNumber: company.licenseNumber || '',
      addresses,
      contacts,
      socialMedia,
      businessHours,
      whatsappNumber: company.whatsappNumber || company.phone,
      tollFreeNumber: undefined,
      shippingSettings: company.shippingSettings
    };
  }

  // Transform pickup location
  private transformPickupLocation(location: PickupLocationResponseDto): PickupLocation {
    return {
      id: location.id,
      name: location.name,
      address: location.address,
      area: location.city, // Using city as area for now
      city: location.city,
      state: location.state,
      zipCode: location.zipCode,
      pincode: location.zipCode,
      phone: location.phone,
      timings: location.openingHours,
      landmark: undefined,
      mapUrl: location.mapUrl,
      isActive: location.isActive,
      transportProviderName: location.transportProviderName
    };
  }

  // Parse business hours from backend format
  private parseBusinessHours(hours?: any): BusinessHours[] {
    const defaultHours: BusinessHours[] = [
      { day: 'Monday', open: '10:00 AM', close: '8:00 PM', isClosed: false },
      { day: 'Tuesday', open: '10:00 AM', close: '8:00 PM', isClosed: false },
      { day: 'Wednesday', open: '10:00 AM', close: '8:00 PM', isClosed: false },
      { day: 'Thursday', open: '10:00 AM', close: '8:00 PM', isClosed: false },
      { day: 'Friday', open: '10:00 AM', close: '8:00 PM', isClosed: false },
      { day: 'Saturday', open: '10:00 AM', close: '9:00 PM', isClosed: false },
      { day: 'Sunday', open: '11:00 AM', close: '7:00 PM', isClosed: false }
    ];

    if (!hours || typeof hours !== 'object') {
      return defaultHours;
    }

    // Parse backend business hours format if available
    return defaultHours.map(day => {
      const dayHours = hours[day.day.toLowerCase()];
      if (dayHours && typeof dayHours === 'object') {
        return {
          day: day.day,
          open: dayHours.open || day.open,
          close: dayHours.close || day.close,
          isClosed: dayHours.isClosed || false
        };
      }
      return day;
    });
  }

  // Get company information (public endpoint)
  async getCompanyInfo(): Promise<ServiceResponse<CompanyInfo>> {
    try {
      const company = await apiRequest<CompanyResponseDto>(API_ENDPOINTS.COMPANY.PUBLIC);
      
      return {
        success: true,
        data: this.transformCompany(company)
      };
    } catch (error) {
      console.error('Error fetching company info:', error);
      return {
        success: false,
        data: {} as CompanyInfo,
        error: error instanceof Error ? error.message : 'Failed to fetch company information'
      };
    }
  }

  // Get specific address by type
  async getAddressByType(type: CompanyAddress['type']): Promise<ServiceResponse<CompanyAddress | null>> {
    try {
      const companyResponse = await this.getCompanyInfo();
      if (!companyResponse.success) {
        throw new Error(companyResponse.error);
      }
      
      const address = companyResponse.data.addresses.find(addr => addr.type === type);
      
      return {
        success: true,
        data: address || null
      };
    } catch (error) {
      console.error('Error fetching address:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch address'
      };
    }
  }

  // Get contact by type
  async getContactByType(type: ContactInfo['type']): Promise<ServiceResponse<ContactInfo | null>> {
    try {
      const companyResponse = await this.getCompanyInfo();
      if (!companyResponse.success) {
        throw new Error(companyResponse.error);
      }
      
      const contact = companyResponse.data.contacts.find(c => c.type === type);
      
      return {
        success: true,
        data: contact || null
      };
    } catch (error) {
      console.error('Error fetching contact:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch contact information'
      };
    }
  }

  // Get all pickup locations with filtering options
  async getPickupLocations(filters?: { 
    transportProviderName?: string;
    search?: string;
    limit?: number;
  }): Promise<ServiceResponse<PickupLocation[]>> {
    try {
      let url = API_ENDPOINTS.COMPANY.PICKUP_LOCATIONS;
      
      // Add query parameters if filters are provided
      if (filters) {
        const params = new URLSearchParams();
        
        // Add search parameter if provided
        if (filters.search) {
          params.append('search', filters.search);
        }
        
        // Add transport provider name if specified
        if (filters.transportProviderName) {
          params.append('transportProviderName', filters.transportProviderName);
        }
        
        // Add limit parameter if specified
        if (filters.limit) {
          params.append('limit', filters.limit.toString());
        }
        
        url += '?' + params.toString();
      }
      
      const locations = await apiRequest<PickupLocationResponseDto[]>(url);
      
      const transformedLocations = locations
        .filter(loc => loc.isActive)
        .map(loc => this.transformPickupLocation(loc));
      
      return {
        success: true,
        data: transformedLocations
      };
    } catch (error) {
      console.error('Error fetching pickup locations:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch pickup locations'
      };
    }
  }

  // Get pickup location by ID
  async getPickupLocationById(id: string): Promise<ServiceResponse<PickupLocation | null>> {
    try {
      const locationsResponse = await this.getPickupLocations();
      if (!locationsResponse.success) {
        throw new Error(locationsResponse.error);
      }
      
      const location = locationsResponse.data.find(loc => loc.id === id);
      
      return {
        success: true,
        data: location || null
      };
    } catch (error) {
      console.error('Error fetching pickup location:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch pickup location'
      };
    }
  }

  // Check if store is open
  async isStoreOpen(): Promise<ServiceResponse<boolean>> {
    try {
      const companyResponse = await this.getCompanyInfo();
      if (!companyResponse.success) {
        throw new Error(companyResponse.error);
      }

      const now = new Date();
      const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });

      const todayHours = companyResponse.data.businessHours.find(
        hours => hours.day === dayOfWeek
      );

      if (!todayHours || todayHours.isClosed) {
        return {
          success: true,
          data: false
        };
      }

      // Simple time comparison (this is a basic implementation)
      // const currentTime = now.toLocaleTimeString('en-US', { hour12: true });
      // In production, use a proper time library for accurate comparison
      
      return {
        success: true,
        data: true // Simplified for now
      };
    } catch (error) {
      console.error('Error checking store status:', error);
      return {
        success: false,
        data: false,
        error: error instanceof Error ? error.message : 'Failed to check store status'
      };
    }
  }

  // Get business hours for a specific day
  async getBusinessHours(day?: string): Promise<ServiceResponse<BusinessHours[]>> {
    try {
      const companyResponse = await this.getCompanyInfo();
      if (!companyResponse.success) {
        throw new Error(companyResponse.error);
      }
      
      if (day) {
        const hours = companyResponse.data.businessHours.filter(
          h => h.day.toLowerCase() === day.toLowerCase()
        );
        return {
          success: true,
          data: hours
        };
      }
      
      return {
        success: true,
        data: companyResponse.data.businessHours
      };
    } catch (error) {
      console.error('Error fetching business hours:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch business hours'
      };
    }
  }

  // Get social media links
  async getSocialMediaLinks(): Promise<ServiceResponse<SocialMedia[]>> {
    try {
      const companyResponse = await this.getCompanyInfo();
      if (!companyResponse.success) {
        throw new Error(companyResponse.error);
      }
      
      return {
        success: true,
        data: companyResponse.data.socialMedia
      };
    } catch (error) {
      console.error('Error fetching social media links:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch social media links'
      };
    }
  }
}

export const companyService = new CompanyService();